import { BaseTreeNode, ToolbarNavigationState, TreeState } from '../models/tree-node';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TreeSortMode } from '../../shared/models';
import { EmitterService } from '../../shared/services/emitter.service';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseTreeService<T extends BaseTreeNode> {
  protected state: TreeState<T> = {
    root: null,
    paths: {},
    selectedNode: null,
    expandedNodes: [],
  };

  protected navigationState: ToolbarNavigationState<T> = {
    node: null,
    action: 'default',
  };

  protected state$: BehaviorSubject<TreeState<T>> = new BehaviorSubject<TreeState<T>>(this.state);
  protected toolbarNavigation$: BehaviorSubject<ToolbarNavigationState<T>> = new BehaviorSubject<
    ToolbarNavigationState<T>
  >(this.navigationState);

  public getState(): Observable<TreeState<T>> {
    return this.state$.asObservable();
  }

  public getCurrentState(): TreeState<T> {
    return this.state;
  }

  public getToolbarNavigationState(): Observable<ToolbarNavigationState<T>> {
    return this.toolbarNavigation$.asObservable();
  }

  public hasPathId(nodeId: string): boolean {
    return nodeId in this.state.paths && this.state.paths[nodeId]?.length > 0;
  }

  public getPathIds(nodeId: string): string[] | undefined {
    return this.state.paths[nodeId];
  }

  public findNodeById(id: string): T | null {
    return this.findNode(this.state.root, id);
  }

  protected findNode(node: T | null, id: string): T | null {
    if (!node) return null;

    if (node.id === id) return node;

    const pathIds: string[] | undefined = this.state.paths[id];
    if (pathIds) {
      // Use path information to traverse directly to the node
      let currentNode: T = node;
      for (let i: number = 1; i < pathIds.length; i++) {
        const childId: string = pathIds[i];
        const child: T | undefined = currentNode.children.find((c) => c.id === childId) as T;
        if (!child) return null;
        currentNode = child;
      }
      return currentNode;
    }

    // fallback to depth-first search
    if (node.id === id) return node;

    for (const child of node.children) {
      const found: T | null = this.findNode(child as T, id);
      if (found) return found;
    }

    return null;
  }

  protected findActiveNodes(node: T): T[] {
    const activeNodes: T[] = [];

    if (node.isSelected || node.isExpanded) {
      activeNodes.push(node);
    }

    for (const child of node.children) {
      activeNodes.push(...this.findActiveNodes(child as T));
    }

    return activeNodes;
  }

  public inactivateAllNodes(): void {
    if (!this.state.root) return;

    const currentlyActiveNodes: T[] = this.findActiveNodes(this.state.root);
    currentlyActiveNodes.forEach((node: T): void => {
      node.isSelected = false;
      node.isExpanded = false;
    });
  }

  public selectNode(node: T): void {
    this.updateState({
      selectedNode: node,
    });
  }

  public selectNodeById(id: string): void {
    const node: T | null = this.findNodeById(id);
    if (node) {
      this.selectNode(node);
    }
  }

  public setActiveNodeById(id: string, saveState: boolean = true): void {
    const node: T | null = this.findNodeById(id);
    if (!node) {
      return;
    }

    this.setActiveNode(node, saveState);
  }

  //similar to selectNodeById but also ensure only node and its parents are selected
  public setActiveNode(node: T, saveState: boolean = true): void {
    // First inactivate all nodes
    this.inactivateAllNodes();

    // Set the target node and its ancestors as expanded
    const expandedNodes: T[] = [];
    let currentNode: T | null = node;
    while (currentNode) {
      currentNode.isExpanded = true;
      expandedNodes.push(currentNode);
      currentNode = currentNode.parent as T;
    }

    // Set the target node as selected
    node.isSelected = true;

    if (saveState) {
      this.updateState({
        root: this.state.root,
        expandedNodes,
        selectedNode: node,
      });
    }
  }

  //direct navigation for toolbar purpose
  public navigateToNodeId(id: string): void {
    this.setActiveNodeById(id, true);
    this.updateToolbarNavigationState({
      node: this.state.selectedNode,
      action: 'direct',
    });
  }

  public previous(saveState: boolean): void {
    if (!this.state.root) {
      return;
    }

    const action = 'previous';
    const currentNode: T | null = this.state.selectedNode;

    // If no node is selected, select the root
    if (!currentNode) {
      this.setActiveNode(this.state.root, saveState);
      this.updateToolbarNavigationState({
        node: this.state.root,
        action,
      });
      return;
    }

    // If current node is root, stay at root
    if (currentNode === this.state.root) {
      return;
    }

    // Try to find previous sibling
    const { parent } = currentNode;
    if (parent) {
      const currentIndex: number = parent.children.indexOf(currentNode);
      if (currentIndex > 0) {
        // Move to previous sibling
        this.setActiveNode(parent.children[currentIndex - 1] as T, saveState);
        this.updateToolbarNavigationState({
          node: parent.children[currentIndex - 1] as T,
          action,
        });
        return;
      }

      // If no previous sibling, move to parent
      this.setActiveNode(parent as T, saveState);
      this.updateToolbarNavigationState({
        node: parent as T,
        action,
      });
    }
  }

  public next(saveState: boolean = true): void {
    if (!this.state.root) {
      return;
    }

    const action = 'next';

    const currentNode: T | null = this.state.selectedNode;

    // If no node is selected, select the root
    if (!currentNode) {
      this.setActiveNode(this.state.root, saveState);
      this.updateToolbarNavigationState({
        node: this.state.root,
        action,
      });
      return;
    }

    // If current node has children, move to first child
    if (currentNode.children.length > 0) {
      this.setActiveNode(currentNode.children[0] as T, saveState);
      this.updateToolbarNavigationState({
        node: currentNode.children[0] as T,
        action,
      });
      return;
    }

    // If no children, try to find next sibling
    const { parent } = currentNode;
    if (parent) {
      const currentIndex: number = parent.children.indexOf(currentNode);
      if (currentIndex < parent.children.length - 1) {
        // Move to next sibling
        this.setActiveNode(parent.children[currentIndex + 1] as T, saveState);
        this.updateToolbarNavigationState({
          node: parent.children[currentIndex + 1] as T,
          action,
        });
        return;
      }
    }

    // If at last sibling, find next parent sibling
    let currentParent: BaseTreeNode | null = parent;
    while (currentParent) {
      const grandParent: BaseTreeNode | null = currentParent.parent;
      if (grandParent) {
        const parentIndex: number = grandParent.children.indexOf(currentParent);
        if (parentIndex < grandParent.children.length - 1) {
          // Move to parent's next sibling
          this.setActiveNode(grandParent.children[parentIndex + 1] as T, saveState);
          this.updateToolbarNavigationState({
            node: grandParent.children[parentIndex + 1] as T,
            action,
          });
          return;
        }
      }
      currentParent = grandParent;
    }

    // If we reach here, we're at the last node in the tree
    // Optionally, you could wrap around to the root or stay at current node
    this.setActiveNode(this.state.root, saveState);
    this.toolbarNavigation$.next({
      node: this.state.root,
      action,
    });
  }

  public navigateToHome(saveState: boolean = true): void {
    if (this.state.selectedNode && this.state.root.type !== this.state.selectedNode.type) {
      this.setActiveNode(this.state.root, saveState);
    } else {
      EmitterService.get('SUBSECTION_NAVIGATION:DASHBOARD').emit();
    }
  }

  protected updateState(update: Partial<TreeState<T>>): void {
    this.state = {
      ...this.state,
      ...update,
    };
    this.state$.next(this.state);
  }

  protected updateToolbarNavigationState(update: Partial<ToolbarNavigationState<T>>): void {
    this.navigationState = {
      ...this.navigationState,
      ...update,
    };
    this.toolbarNavigation$.next(this.navigationState);
  }

  public addNodeToParent(parent: T, newNode: T, updateState: boolean = true): T {
    // Update pathIds for the new node
    this.state.paths[newNode.id] = this.state.paths[parent.id]?.concat(newNode.id) || [newNode.id];

    // Add to parent's children
    parent.children.push(newNode);
    newNode.parent = parent;

    // Update state
    this.updateState({
      root: this.state.root,
    });

    return newNode;
  }

  /**
   * Adds a new node to the tree under the specified parent node
   * @param parentId The ID of the parent node
   * @param newNode The new node to add
   * @param updateState Whether to update the state after adding
   * @returns The added node if successful, null otherwise
   */
  public addNode(parentId: string, newNode: T, updateState: boolean = true): T | null {
    const parentNode: T | null = this.findNodeById(parentId);
    if (!parentNode) return null;

    this.addNodeToParent(parentNode, newNode, updateState);
  }

  /**
   * Updates an existing node's data
   * @param nodeId The ID of the node to update
   * @param updateData The new data to update
   * @returns The updated node if successful, null otherwise
   */
  public updateNode(nodeId: string, updateData: Partial<T>): T | null {
    const node: T | null = this.findNodeById(nodeId);
    if (!node) return null;

    // Update node data
    Object.assign(node, updateData);

    // Update state
    this.updateState({
      root: this.state.root,
    });

    return node;
  }

  /**
   * Deletes a node from the tree
   * @param nodeId The ID of the node to delete
   * @param updateState Whether to update the state after deletion
   * @returns The parent node if successful, null otherwise
   */
  public deleteNodeById(nodeId: string, updateState: boolean = true): T | null {
    const node: T | null = this.findNodeById(nodeId);
    if (!node || !node.parent) return null;

    return this.deleteNode(node, updateState);
  }

  public deleteAllChildren(node: T, updateState: boolean = true): void {
    const { children } = node;
    for (let i: number = children.length - 1; i >= 0; i--) {
      const child: T = children[i] as T;
      this.deleteNode(child, false);
    }

    if (updateState) {
      this.updateState({
        root: this.state.root,
      });
    }
  }

  public deleteNode(node: T, updateState: boolean = true): T | null {
    // Remove from parent's children
    const { parent } = node;
    const index: number = parent.children.findIndex((child: T) => child.id === node.id);
    if (index === -1) return null;

    parent.children.splice(index, 1);

    // Clean up paths
    delete this.state.paths[node.id];

    // Update state
    if (updateState) {
      this.updateState({
        root: this.state.root,
        selectedNode: this.state.selectedNode,
      });
    }

    return parent as T;
  }

  public clearState(): void {
    this.updateState({
      root: null,
      paths: {},
      selectedNode: null,
      expandedNodes: [],
    });
  }

  public clearSelectedNode(): void {
    this.inactivateAllNodes();

    this.updateState({
      selectedNode: null,
      expandedNodes: [],
      root: this.state.root,
    });
  }

  public setActiveRoot(): void {
    this.setActiveNode(this.state.root, true);
  }

  public updateLoadingState(loading: boolean): void {
    this.updateState({
      loading,
    });
  }

  public abstract sortTree(sortMode: TreeSortMode, updateState?: boolean): void;
}
